import { styled } from '@mui/material';
import type { ComponentProps } from 'react';
import { forwardRef, useRef, useState } from 'react';
import { IMaskMixin } from 'react-imask';

import { ChkInput } from '../ChkInput/ChkInput';

import { TEST_IDS } from './ChkMaskedInput.constants';
import type {
  ChkMaskedInputProps,
  MaskedInputProps,
} from './ChkMaskedInput.types';

export const ChkMaskedInput = ({
  placeholder,
  prompt,
  ...props
}: ChkMaskedInputProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [promptInputValue, setPromptInputValue] = useState('');
  const [promptPlaceholder, setPromptPlaceholder] = useState(prompt?.(''));

  return (
    <MaskedInput
      inputRef={ref}
      {...props}
      onAccept={(value, mask) => {
        setPromptInputValue(mask.value);
        setPromptPlaceholder(prompt?.(mask.value).slice(mask.value.length));
        props?.onAccept?.(value, mask);
      }}
      placeholder={placeholder}
      promptInputValue={promptInputValue}
      promptPlaceholder={promptPlaceholder}
    />
  );
};

export const MaskedInput = IMaskMixin(
  ({
    inputRef,
    promptInputValue,
    promptPlaceholder,
    'aria-required': ariaRequired,
    'aria-invalid': ariaInvalid,
    ...props
  }: MaskedInputProps) => {
    return (
      <ChkInput
        {...props}
        inputRef={inputRef}
        slots={{
          input: InputSlot,
        }}
        slotProps={{
          input: {
            promptPlaceholder,
            promptInputValue,
            'aria-required': ariaRequired,
            'aria-invalid': ariaInvalid,
          } as InputSlotProps,
        }}
      />
    );
  }
);

type InputSlotProps = ComponentProps<typeof UnstyledInput> & {
  promptInputValue: string;
  promptPlaceholder: string;
};

const InputSlot = forwardRef<HTMLInputElement, InputSlotProps>(
  ({ promptPlaceholder, promptInputValue, ...props }, ref) => {
    return (
      <InputRoot>
        {promptInputValue.length > 0 && (
          <Placeholder
            data-testid={TEST_IDS.promptPlaceholder}
            aria-hidden={true}
          >
            <HiddenPlaceholder>{promptInputValue}</HiddenPlaceholder>
            {promptPlaceholder}
          </Placeholder>
        )}
        <UnstyledInput {...props} ref={ref} />
      </InputRoot>
    );
  }
);

const HiddenPlaceholder = styled('span')({
  color: 'transparent',
});

const InputRoot = styled('div')({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
});

const Placeholder = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  color: theme.tokens.inputValueDefault,
  opacity: 1,
  pointerEvents: 'none',
  whiteSpace: 'nowrap',
}));

const UnstyledInput = styled('input')({
  font: 'inherit',
  letterSpacing: 'inherit',
  color: 'currentColor',
  border: 0,
  padding: 0,
  background: 'none',
  margin: 0,
  WebkitTapHighlightColor: 'transparent',
  display: 'block',
  minWidth: 0,
  width: '100%',
  '&:focus': {
    outline: 0,
  },
  '&:invalid': {
    boxShadow: 'none',
  },
});
